import { paths } from 'config/path_helpers';
import fetch from 'utilities/fetch';

import { genericErrorMessage, PaymentError } from './errors';
import { SubscriptionSubmissionResponse } from './types';

export const confirmStripePaymentRequest = async (stripe, clientSecret, paymentMethodId) => {
  const response = await stripe.confirmCardPayment(
    clientSecret,
    { payment_method: paymentMethodId },
    { handleActions: false }
  );

  if (response.error) {
    const debugValues = {
      clientSecret,
      paymentMethodId
    };
    throw new PaymentError(
      genericErrorMessage,
      `Error in confirmStripePaymentRequest with values ${JSON.stringify(debugValues)} and response ${JSON.stringify(response)}`
    );
  } else if (response.paymentIntent.status === 'requires_action') {
    return await confirmStripePayment(stripe, response.clientSecret);
  } else {
    return { nextStepUrl: response.nextStepUrl };
  }
};

export const confirmStripePayment = async (stripe, clientSecret, paymentMethodId = null, nextStepUrl = null) => {
  const options: any = {};
  if (paymentMethodId) {
    options.payment_method = paymentMethodId;
    options.setup_future_usage = 'off_session';
  }
  if (nextStepUrl) {
    options.return_url = nextStepUrl
  }

  const response = await stripe.confirmCardPayment(clientSecret, options);

  if (response.error) {
    const debugValues = {
      clientSecret,
      paymentMethodId,
      nextStepUrl
    };
    throw new PaymentError(
      genericErrorMessage,
      `Error in confirmStripePayment with values ${JSON.stringify(debugValues)} and response ${JSON.stringify(response)}`
    );
  } else {
    return { nextStepUrl: nextStepUrl };
  }
};

export const createStripePaymentMethod = async (stripe, card, values) => {
  const {  cardName, cardZipCode, email } = values;
  const response = await stripe.createPaymentMethod({
    type: 'card',
    card,
    billing_details: {
      name: cardName,
      email: email || null,
      address: {
        postal_code: cardZipCode
      }
    }
  });

  if (response.error) {
    throw new PaymentError(
      parseStripeError(response.error),
      `Error in createStripePaymentMethod with values ${JSON.stringify(values)} and response ${JSON.stringify(response)}`
    );
  } else {
    return { stripePaymentMethod: response.paymentMethod };
  }
};

export const createStripePaymentRequest = async (stripe, name, priceCents) => {
  const response = await stripe.paymentRequest({
    country: 'US',
    currency: 'usd',
    total: {
      label: name,
      amount: priceCents
    },
    requestPayerName: true,
    requestPayerEmail: false
  });

  if (response.error) {
    const debugValues = {
      name,
      priceCents
    };
    throw new PaymentError(
      null,
      `Error in createStripePaymentRequest with values ${JSON.stringify(debugValues)} and response ${JSON.stringify(response)}`
    );
  } else {
    return response;
  }
};

export const createStripeSource = async (stripe, values) => {
  const { amount, currency } = values;
  const response = await stripe.createSource({
    type: 'alipay',
    amount,
    currency,
    redirect: {
      return_url: `${window.location.origin}${paths.welcome('true')}&stripe=true`
    }
  });

  if (!response.source) {
    throw new PaymentError(
      'We ran into an issue setting up your Alipay payment. Please try again and if you are still having trouble, contact us at <a href="mailto:help@magoosh.com">help@magoosh.com</a>.',
      `Error in createStripeSource with values ${JSON.stringify(values)} and response ${JSON.stringify(response)}`
    );
  } else {
    return { alipayPaymentUrl: response.source.redirect.url, purchaseToken: response.source.id };
  }
};

export const createStripeToken = async (stripe, card): Promise<{ purchaseToken: string; }> => {
  const response = await stripe.createToken(card);

  if (response.error) {
    throw new PaymentError(
      parseStripeError(response.error),
      `Error in createStripeToken with response ${JSON.stringify(response)}`
    );
  } else {
    return { purchaseToken: response.token.id };
  }
};

export const createOrder = async (stripe, values): Promise<{ nextStepUrl: string; }> => {
  const response: SubscriptionSubmissionResponse = await fetch(
    paths.api.orders(),
    {
      method: 'POST',
      body: JSON.stringify(values)
    }
  );

  if (response._redirect) {
    return { nextStepUrl: response._redirect };
  } else if (response.actionRequired === 'confirmPayment') {
    return await confirmStripePayment(
      stripe,
      response.clientSecret,
      response.paymentMethodId,
      response.nextStepUrl
    );
  } else {
    throw new PaymentError(
      genericErrorMessage,
      `Unexpected response in createOrder with values ${JSON.stringify(values)} and response ${JSON.stringify(response)}`
    );
  }
}

export const createSubscription = async (stripe, values, setupPaymentRequestListeners = null): Promise<{ nextStepUrl?: string; actionRequired?: string; }> => {
  const response: SubscriptionSubmissionResponse = await fetch(
    paths.api.subscriptions(),
    {
      method: 'POST',
      body: JSON.stringify(values)
    }
  );

  if (response._redirect) {
    return { nextStepUrl: response._redirect };
  } else if (response.actionRequired === 'confirmPayment') {
    return await confirmStripePayment(
      stripe,
      response.clientSecret,
      response.paymentMethodId,
      response.nextStepUrl
    );
  } else if (response.actionRequired === 'completePaymentRequest') {
    if (setupPaymentRequestListeners) {
      setupPaymentRequestListeners(response);
      return { actionRequired: response.actionRequired };
    } else {
      throw new PaymentError(
        genericErrorMessage,
        `Unexpected state in createSubscription (completePaymentRequest action required but no setupPaymentRequestListeners callback provided) with values ${JSON.stringify(values)} and response ${JSON.stringify(response)}`
      );
    }
  } else {
    throw new PaymentError(
      genericErrorMessage,
      `Unexpected response in createSubscription with values ${JSON.stringify(values)} and response ${JSON.stringify(response)}`
    );
  }
};

export const payRemainingBalance = async (stripe, values, subscriptionId) => {
  const response = await fetch(paths.api.subscriptionPayRemainingBalance(subscriptionId), {
    method: 'PUT',
    body: JSON.stringify(values)
  });

  if (response._redirect) {
    return { nextStepUrl: response._redirect };
  } else if (response.actionRequired) {
    return confirmStripePayment(stripe, response.clientSecret, response.paymentMethodId, response.nextStepUrl);
  }
};

export const validateCoupon = async (subscriptionPlanId, couponCode): Promise<{ couponCode: string; discount: any; }> => {
  try {
    const response = await fetch(
      paths.api.validateCoupon(subscriptionPlanId, couponCode, true),
      {
        method: 'GET'
      }
    );

    if (response.couponCode) {
      return response;
    } else {
      throw new Error("We weren't able to apply that promo code");
    }
  } catch(error) {
    throw new Error("We weren't able to apply that promo code");
  }
};

const parseStripeError = (error) => {
  if (error.code === 'email_invalid') {
    return { email: error.message };
  } else if (error.type === 'card_error' || error.type === 'validation_error') {
    if (error.message.indexOf('expiration date') !== -1 || error.message.indexOf('expiration year') !== -1) {
      return { stripeExpirationDate: error.message };
    } else if (error.message.indexOf('card number') !== -1) {
      return { stripeCardNumber: error.message };
    } else if (error.message.indexOf('security code') !== -1) {
      return { stripeSecurityCode: error.message };
    } else {
      return { _error: error.message };
    }
  } else {
    return { _error: error.message };
  }
};
